import {
  FEATURES,
  GROUP_TYPE,
  IBroadcastInfo,
  IGroupInfo,
  IInfoType,
  IUserInfo,
} from "@home-health-notify/domain";

import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import CalendarIcon from "@mui/icons-material/Event";
import { Box, IconButton } from "@mui/material";
import { OrganizationContext } from "hhn-commons";
import { useContext, useState } from "react";
import { AddBroadcastGroup } from "../addBroadcastGroup/AddBroadcastGroup";
import { AddInternalGroup } from "../addInternalGroup/AddInternalGroup";
import AddPatientGroup from "../addPatientGroup/AddPatientGroup";
import { Calendar } from "../calendar/Calendar";
import { RightDrawerContext } from "../context/RightDrawerContext";
import { DrawerHeader, EmptyBox } from "../layout/RightDrawer.style";
import ProgressLoader from "../loader/ProgressLoader";
import { CommonChatHeaderDescription } from "../message/messageHeader/CommonChatHeaderDescription";
import { PatientHeaderDescription } from "../message/messageHeader/patientGroupHeader/PatientHeaderDescription";
import PrivateChatInfo from "./PrivateChatInfo";
import { GroupHeaderTabs } from "./tabs/GroupHeaderTabs";

const ChatInfoContent = ({
  info,
  tabValue,
}: {
  info: IInfoType;
  tabValue?: number;
}) => {
  const { setContent, closeDrawer } = useContext(RightDrawerContext);
  const { selectedOrgUser } = useContext(OrganizationContext);
  const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false);

  const isPrivateChat = info && info.groupType === GROUP_TYPE.PRIVATE;
  const isPatientGroup = info && info.groupType === GROUP_TYPE.PATIENT;
  const isBroadcastGroup = info && info.groupType === GROUP_TYPE.BROADCAST;
  const isMember = info && (info as IGroupInfo | IBroadcastInfo).isMember;
  const canEditGroup = !!selectedOrgUser?.hasAccess(FEATURES.EDIT_GROUP);
  const canOpenPrivateCalendar = !!selectedOrgUser?.hasAccess(
    FEATURES.OPEN_PRIVATE_CHAT_CALENDAR,
  );
  const canOpenCalendar = isPrivateChat ? canOpenPrivateCalendar : isMember;

  const editGroup = (info: IGroupInfo | IBroadcastInfo) => {
    if (info.groupType === GROUP_TYPE.PATIENT) {
      setContent(<AddPatientGroup mode="edit" chatId={info.id} />);
    } else if (info.groupType === GROUP_TYPE.BROADCAST) {
      setContent(<AddBroadcastGroup chatId={info.id} />);
    } else {
      setContent(<AddInternalGroup chatId={info.id} />);
    }
  };

  const openCalendar = () => {
    setIsCalendarOpen(true);
  };

  const closeCalendar = () => {
    setIsCalendarOpen(false);
  };
  return (
    <>
      <Calendar
        open={isCalendarOpen}
        closeModal={closeCalendar}
        info={info as IGroupInfo | IUserInfo}
      />
      <>
        <DrawerHeader>
          {info && (
            <Box sx={{ padding: "6px 15px 5px", flex: 1, minWidth: 0 }}>
              {isPatientGroup && (
                <PatientHeaderDescription
                  name={(info as IGroupInfo).name}
                  mrn={(info as IGroupInfo).mrn}
                  patientStatus={(info as IGroupInfo).patientStatus}
                  servicesToPatient={(info as IGroupInfo).servicesToPatient}
                  meta={(info as IGroupInfo)?.meta}
                />
              )}
              {!isPatientGroup && !isPrivateChat && (
                <CommonChatHeaderDescription chatInfo={info as IGroupInfo} />
              )}
              {isPrivateChat && (
                <CommonChatHeaderDescription
                  chatInfo={info as IUserInfo}
                  showUserDescription={true}
                />
              )}
            </Box>
          )}
          {isPrivateChat && <EmptyBox />}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: 120,
              minWidth: 120,
              justifyContent: "flex-end",
            }}
          >
            {canOpenCalendar ? (
              <IconButton
                data-testid="calendar-icon"
                size="medium"
                onClick={openCalendar}
              >
                <CalendarIcon />
              </IconButton>
            ) : null}
            {(isMember || isBroadcastGroup) && canEditGroup && (
              <IconButton
                data-testid="group-edit-icon"
                size="medium"
                onClick={() => editGroup(info as IGroupInfo | IBroadcastInfo)}
              >
                <EditIcon />
              </IconButton>
            )}
            <IconButton
              size="medium"
              onClick={closeDrawer}
              data-testid="chat-info-close"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DrawerHeader>
        {info && !isPrivateChat && (
          <GroupHeaderTabs groupInfo={info as IGroupInfo} tabValue={tabValue} />
        )}
        {info && isPrivateChat && (
          <PrivateChatInfo userInfo={info as IUserInfo} />
        )}
        {!info && <ProgressLoader />}
      </>
    </>
  );
};

export default ChatInfoContent;
